import {view, Action, Tooltip, Badge} from '@sail/ui';
import {useIntl} from 'react-intl';
import {noTranslateMessages} from '../helpers/noTranslate';
import {widgetLinkAction} from '../helpers/widgetLinkAction';
import {
  useWidgetConversationId,
  useWidgetRoutingMode,
} from '../Widget/useWidget';

const devMessages = noTranslateMessages({
  badgeLabel: {
    id: 'conversations_ui.header.dev_badge_label',
    defaultMessage: 'Dev info',
  },
  normalRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.normal_routing_mode_label',
    defaultMessage: 'Routing mode: normal',
  },
  testQueueRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_queue_routing_mode_label',
    defaultMessage: 'Routing mode: test queue',
  },
  conversationTranscriptLabel: {
    id: 'support_conversations_client.header.dev_badge.conversation_transcript_label',
    defaultMessage: 'Conversation transcript',
  },
  forestVersionLabel: {
    id: 'support_conversations_client.header.dev_badge.forest_version_label',
    defaultMessage: 'Forest version: {forestVersion}',
  },
  testAgentModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_agent_mode_label',
    defaultMessage: 'Test agent mode: {testAgentMode}',
  },
});
function isDevBox(url: URL) {
  const host = url.host.toLowerCase();
  return host.endsWith('-mydev.dev.stripe.me');
}

function generateTranscriptLink(origin: string, conversationId?: string) {
  const adminOrigin = origin.replace(/--support_conversations-/, '--admin-');
  return `${adminOrigin}/universal-help/conversation-details/${conversationId}`;
}

export function DevBadge() {
  const {conversationId} = useWidgetConversationId();
  const {routingMode} = useWidgetRoutingMode();
  const intl = useIntl();
  const url = new URL(window.location.href);
  const isTestQueue = routingMode === 'dev' || routingMode === 'synthetic';

  if (!isDevBox(url)) {
    if (isTestQueue) {
      return (
        <Badge type="info" data-testid="dev-badge">
          {intl.formatMessage(devMessages.testQueueRoutingModeLabel)}
        </Badge>
      );
    }

    return null;
  }

  const transcriptLink = generateTranscriptLink(url.origin, conversationId);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const forestVersion = urlSearchParams.get('forestVersion') || 'off';
  const testAgentMode = urlSearchParams.get('test_agent_mode') || 'off';

  return (
    <Tooltip
      trigger={
        <Badge type="info" data-testid="dev-badge">
          {intl.formatMessage(devMessages.badgeLabel)}
        </Badge>
      }
    >
      <view.div css={{stack: 'y', gap: 'small'}}>
        {conversationId ? (
          <Action href={transcriptLink} uses={[widgetLinkAction()]}>
            {intl.formatMessage(devMessages.conversationTranscriptLabel)}
          </Action>
        ) : null}
        <view.div>
          {routingMode === 'normal'
            ? intl.formatMessage(devMessages.normalRoutingModeLabel)
            : null}
          {isTestQueue
            ? intl.formatMessage(devMessages.testQueueRoutingModeLabel)
            : null}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.forestVersionLabel, {
            forestVersion,
          })}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.testAgentModeLabel, {
            testAgentMode,
          })}
        </view.div>
      </view.div>
    </Tooltip>
  );
}
